import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBluelagoon } from "@bluelagoon";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, ExternalLink, ThumbsUp, ThumbsDown, Meh } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PAGE_SIZE = 25;

function rating(r, t) {
    if (r === 1) return <span className="inline-flex items-center gap-1 text-red-500 text-xs"><ThumbsDown className="w-3 h-3" />{t("admin.manageCompany.chats.bad", "Bad")}</span>;
    if (r === 2) return <span className="inline-flex items-center gap-1 text-yellow-500 text-xs"><Meh className="w-3 h-3" />{t("admin.manageCompany.chats.okay", "Okay")}</span>;
    if (r === 3) return <span className="inline-flex items-center gap-1 text-green-500 text-xs"><ThumbsUp className="w-3 h-3" />{t("admin.manageCompany.chats.great", "Great")}</span>;
    return <span className="text-muted-foreground text-xs">—</span>;
}

export default function ChatsTab({ companyId }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [chats, setChats] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const { call: fetchChats, isLoading } = useBluelagoon({ method: "getAllStepChats" });

    const load = useCallback(() => {
        fetchChats({ companyId, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
            .then((res) => {
                setChats(res?.stepChats || []);
                setTotal(res?.pagination?.total || 0);
            })
            .catch(() => setChats([]));
    }, [fetchChats, companyId, page]);

    useEffect(() => { load(); }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-end">
                <span className="text-xs text-muted-foreground">{t("admin.manageCompany.chats.total", "{{count}} chats", { count: total })}</span>
            </div>
            {isLoading && chats.length === 0 ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("admin.manageCompany.chats.workflow", "Workflow")}</TableHead>
                            <TableHead>{t("admin.manageCompany.chats.step", "Step")}</TableHead>
                            <TableHead>{t("admin.manageCompany.chats.rating", "Rating")}</TableHead>
                            <TableHead className="text-right">{t("admin.manageCompany.chats.credits", "Credits")}</TableHead>
                            <TableHead>{t("admin.manageCompany.chats.updated", "Updated")}</TableHead>
                            <TableHead className="w-12" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {chats.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                                {t("admin.manageCompany.chats.empty", "No chats")}
                            </TableCell></TableRow>
                        ) : chats.map((chat) => (
                            <TableRow key={chat.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/admin/chats/${chat.id}`)}>
                                <TableCell className="font-medium">{chat.workflow?.name || "—"}</TableCell>
                                <TableCell className="text-sm">{chat.workflowStep?.name || "—"}</TableCell>
                                <TableCell>{rating(chat.rating, t)}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{chat.totalCredits != null ? chat.totalCredits.toFixed(2) : "—"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{chat.updatedAt ? formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true }) : "—"}</TableCell>
                                <TableCell><ExternalLink className="h-3 w-3 text-muted-foreground" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-xs">
                    <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>{t("common.previous", "Previous")}</Button>
                    <span className="text-muted-foreground">{t("common.pageOf", "Page {{page}} of {{total}}", { page, total: totalPages })}</span>
                    <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t("common.next", "Next")}</Button>
                </div>
            )}
        </div>
    );
}
